/**
 * asyncMessageHelper for using IDI/Lexa service
 * Lexa API confluence page: https://ppaas/api/3719176155268900#apiReference
 * Async Message Availability types: ['AVAILABLE', 'DELAYED_AVAILABLE']; non-eligibility will be take care of inside Lexa team
 */
import axios from 'axios';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { parse, toSeconds } from 'iso8601-duration';
import poll from './poll';
import { asyncChatValidator } from './node-chat-on-page-validator';
import fireImpressionFptiForChatLoadError from './fire-impression-fpti-for-chat-load-error';
import {
  originToEntryPointMapping,
  asyncOrigins,
  asyncPageMapping,
  asyncParamDeviceType,
  chatBotMessageUsRefIdsMapping,
  deviceTypes,
} from '../common/commonConstants';
import Page from './page';
import contentHelper from './contentHelper';

const requestURL = '/smarthelp/mmt-async-check';

// MMT service for getting last active conversation data with conversation id.
// Returning necessary data isConversationActive, serviceError in the form of object to reduce the no of conditional checks.
export const getAsyncLastActiveConversation = () =>
  axios
    .get(requestURL)
    .then((response) => {
      const conversations = response && response.data && response.data.conversations;
      if (conversations && conversations.length) {
        return Promise.resolve({
          isConversationActive: true,
          id: conversations[0].id,
          serviceError: false,
          protocol: conversations[0].protocol,
        });
      }
      return Promise.resolve({ isConversationActive: false, serviceError: false });
    })
    .catch(() => Promise.reject({ isConversationActive: false, serviceError: true }));

//  The IDI channel mapping base on their response; parse IDI option as SAC options
export const IDIChannelMapping = {
  EMAIL: 'email',
  PHONE: 'callus',
  COMMUNITY: 'community',
  RES_CENTER: 'seller',
  CHAT: 'chat',
  MESSAGE_US: 'message',
  CHAT_BOT: 'chatbot',
  SAC: 'scheduleCallBack',
};

// IDI channels filter
export const IDIChannelFilter = function (channels) {
  if (!Array.isArray(channels) || channels.length <= 0 || Object.keys(channels[0]).length === 0) {
    return null;
  }
  const mapping = IDIChannelMapping;

  // Contact opions parser and validation
  return channels.map((channel) =>
    channel.name && mapping[channel.name] && channel.rank
      ? {
          type: mapping[channel.name],
          priority: `${channel.rank}`,
        }
      : {},
  );
};

// Return Async Message estimate wait time in minute
// Duration confluence page: https://en.wikipedia.org/wiki/ISO_8601#Durations
export const getEstimateWaitTime = (waitPeriod) => {
  if (typeof waitPeriod !== 'string' || !waitPeriod) {
    return null;
  }

  const second = toSeconds(parse(waitPeriod));
  return Math.ceil(second / 60);
};

// Aync message contact option validation
export const hasAyncMessage = (channels) =>
  Array.isArray(channels) && channels.some((option) => option.type === 'message');

// Get Message us contact option availability message
const getMessageAvailable = (availabilities) =>
  _find(availabilities, (availability) => availability.name === 'MESSAGE_US');

const eligibilityList = ['AVAILABLE', 'DELAYED_AVAILABLE'];

// checking Async message whether available or not (base on the channel_availability_decisions data)
export const isMessageUsAvailable = (availabilities) => {
  const messageUs = getMessageAvailable(availabilities);
  return !_isEmpty(messageUs) && eligibilityList.indexOf(messageUs.availability_decision) >= 0;
};

export const isAsyncChatEnable = ({ channels, availability }) =>
  hasAyncMessage(channels) && isMessageUsAvailable(availability);

// Get Estimate wait time for Asyc message
export const getWaitTime = (availabilities) => {
  const messageUs = getMessageAvailable(availabilities);
  return getEstimateWaitTime(_get(messageUs, 'wait_time_to_availability.wait_period'));
};

/**
 * Open Async Chat based on the MMT Response.
 * @param params
 * @param mmtResponse
 * If the service returns active conversation pass conversation id to Async chat else pass empty string.
 * If the service fails pass 'INVALID_CONVERSATION' as a parameter to async chat.
 */
export const openAsyncChat = (params, mmtResponse) => {
  let entryPoint;
  const topicName = params.topicRefId || chatBotMessageUsRefIdsMapping.GENERIC;
  
  if (!params.entryPoint) {
    if (params.origin) {
      entryPoint = originToEntryPointMapping[params.origin]
      if (entryPoint === originToEntryPointMapping[asyncOrigins.FINAL_CONTACT_OPTIONS]) {
        entryPoint = contentHelper(originToEntryPointMapping[asyncOrigins.FINAL_CONTACT_OPTIONS], { intentId: topicName});
      }
    }
  }

  const asyncParams = {
    sessionType: 'conversation', hasReply: true, isExtended: false,
    entryPoint,
    conversationProfile: 'HELP_CENTER',
  };
  asyncParams.topicName = topicName;
  asyncParams.deviceType = asyncParamDeviceType[Page.getDeviceType()] || deviceTypes.DESKTOP;
  asyncParams.page = asyncPageMapping[Page.getPageName()];
  asyncParams.origin = params.origin || '';
  asyncParams.subject = params.subject || '';
  if (params.customerIntent) {
    asyncParams.customer_intent = params.customerIntent;
  }
  if (params.intentCode) {
    asyncParams.intent_code = params.intentCode;
  }
  if (mmtResponse && mmtResponse.protocol) {
    asyncParams.protocol = mmtResponse.protocol;
  }

  let id = '';
  if (typeof mmtResponse === 'object' && !_isEmpty(mmtResponse)) {
    if (mmtResponse.isConversationActive && !mmtResponse.serviceError) {
      id = mmtResponse.id;
    } else if (mmtResponse.serviceError) {
      id = 'INVALID_CONVERSATION';
    }
  }
  window.nodeChat.showAsync(id, asyncParams);
};

// Checking and validate nodeChat async function which is a messageUs
const invokingAsyncChat = (params, mmtResponse) =>
  poll(asyncChatValidator, 10, 200)
    .then(() => openAsyncChat(params, mmtResponse))
    .catch(() => {
      fireImpressionFptiForChatLoadError('', '', 'asyncContactUsError');
    });

// Redirect promise direction switcher
const redirectPromiseAfterAsyncConversationCheck = (promise, params = {}) => {
  if (typeof promise !== 'function') {
    return null;
  }

  return promise()
    .then((mmtResponse) => invokingAsyncChat(params, mmtResponse))
    .catch((mmtResponseError) => invokingAsyncChat(params, mmtResponseError));
};

export const initiateAsyncChat = (params = {}) => {
  if(Page.isLoggedIn()) {
    return redirectPromiseAfterAsyncConversationCheck(getAsyncLastActiveConversation, params);
  } if(Page.isLoggedOutChatExp()) {
    return invokingAsyncChat(params, null);
  }
  return null;
};

/**
 * Chatbot async check based on pilot user and new async response from chatbot eligibility API
 * @returns {*|boolean|Boolean}
 */

export const isAsyncUser = (chatBotAsync = false) => Page.isPilotUser() || chatBotAsync;
